import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal } from "lucide-react";
import { ChannelPaymentStatus, ChannelPaymentSubStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { StatusBadge } from "@/components/status-badge";
import { Amount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { MerchantCell } from "@/components/merchant-cell";
import { CHANNEL_PAYMENT_STATUS_COLOR } from "@/lib/status-colors";
import { type TableSearch, useTableState } from "@/lib/table-state";
import { cn, formatStatusLabel } from "@/lib/utils";
import { listChannelPayments } from "@/api/channels";
import { listMerchants } from "@/api/merchants";
import { getInvoiceAssetOptions } from "@/api/invoices";
import { type ChannelPaymentListItem, type ListChannelPaymentsParams } from "@/api/types";

// Task 14 (feature-031 Phase B): platform-wide Channel Payments list — same
// list-page shape as _app.channels.tsx / _app.invoices.tsx. `channelId`
// (per Task 8's backend filter set) is deliberately not exposed as its own
// UI filter here — the channel detail page's own Payments sub-table already
// covers that case by passing it directly, and there's no role-safe options
// source to back a channel-picker dropdown on this page.
type ChannelPaymentFilters = {
  status?: ChannelPaymentStatus;
  subStatus?: ChannelPaymentSubStatus;
  merchantId?: string;
  assetId?: string;
  amountUsdMin?: string;
  amountUsdMax?: string;
  from?: string;
  to?: string;
};

const DEFAULT_FILTERS: ChannelPaymentFilters = {};

const DIALOG_FILTER_KEYS = [
  "subStatus",
  "merchantId",
  "assetId",
  "amountUsdMin",
  "amountUsdMax",
  "from",
  "to",
] as const satisfies ReadonlyArray<keyof ChannelPaymentFilters>;

function validateChannelPaymentsSearch(search: Record<string, unknown>): TableSearch<ChannelPaymentFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    sortBy: typeof search.sortBy === "string" ? search.sortBy : undefined,
    sortOrder: search.sortOrder === "asc" || search.sortOrder === "desc" ? search.sortOrder : undefined,
    status: typeof search.status === "string" ? (search.status as ChannelPaymentStatus) : undefined,
    subStatus: typeof search.subStatus === "string" ? (search.subStatus as ChannelPaymentSubStatus) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
    assetId: typeof search.assetId === "string" ? search.assetId : undefined,
    amountUsdMin: typeof search.amountUsdMin === "string" ? search.amountUsdMin : undefined,
    amountUsdMax: typeof search.amountUsdMax === "string" ? search.amountUsdMax : undefined,
    from: typeof search.from === "string" ? search.from : undefined,
    to: typeof search.to === "string" ? search.to : undefined,
  };
}

export const Route = createFileRoute("/_app/channel-payments")({
  validateSearch: validateChannelPaymentsSearch,
  component: ChannelPaymentsPage,
});

function formatDate(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

const SELECT_CLASSNAME = cn(
  "h-9 rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

const INPUT_CLASSNAME = cn(
  "h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

function RowLink({ channelPaymentId, children }: { channelPaymentId: string; children: ReactNode }) {
  return (
    <Link to="/channel-payments/$channelPaymentId" params={{ channelPaymentId }} className="group -m-2 block p-2">
      {children}
    </Link>
  );
}

function ChannelPaymentFiltersDialog({
  filters,
  setFilter,
  merchants,
  assetOptions,
}: {
  filters: ChannelPaymentFilters;
  setFilter: <K extends keyof ChannelPaymentFilters>(key: K, value: ChannelPaymentFilters[K]) => void;
  merchants: Array<{ id: string; businessName: string }>;
  assetOptions: Array<{ id: string; symbol: string; network: string }>;
}) {
  const [open, setOpen] = useState(false);
  const activeCount = DIALOG_FILTER_KEYS.filter((key) => filters[key] !== undefined).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-1.5">
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Filters
          {activeCount > 0 && (
            <span className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {activeCount}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Filter channel payments</DialogTitle>
          <DialogDescription>Narrow the list by sub-status, merchant, asset, amount, or date.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="filter-subStatus">Sub-status</Label>
            <select
              id="filter-subStatus"
              className={cn(SELECT_CLASSNAME, "w-full")}
              value={filters.subStatus ?? "ALL"}
              onChange={(e) =>
                setFilter("subStatus", e.target.value === "ALL" ? undefined : (e.target.value as ChannelPaymentSubStatus))
              }
            >
              <option value="ALL">All sub-statuses</option>
              {Object.values(ChannelPaymentSubStatus).map((s) => (
                <option key={s} value={s}>
                  {formatStatusLabel(s)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-merchant">Merchant</Label>
            <select
              id="filter-merchant"
              className={cn(SELECT_CLASSNAME, "w-full")}
              value={filters.merchantId ?? "ALL"}
              onChange={(e) => setFilter("merchantId", e.target.value === "ALL" ? undefined : e.target.value)}
            >
              <option value="ALL">All merchants</option>
              {merchants.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.businessName}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-asset">Asset</Label>
            <select
              id="filter-asset"
              className={cn(SELECT_CLASSNAME, "w-full")}
              value={filters.assetId ?? "ALL"}
              onChange={(e) => setFilter("assetId", e.target.value === "ALL" ? undefined : e.target.value)}
            >
              <option value="ALL">All assets</option>
              {assetOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.symbol} · {formatStatusLabel(a.network)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-min">Min amount (USD)</Label>
            <input
              id="filter-min"
              placeholder="No minimum"
              className={INPUT_CLASSNAME}
              value={filters.amountUsdMin ?? ""}
              onChange={(e) => setFilter("amountUsdMin", e.target.value || undefined)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-max">Max amount (USD)</Label>
            <input
              id="filter-max"
              placeholder="No maximum"
              className={INPUT_CLASSNAME}
              value={filters.amountUsdMax ?? ""}
              onChange={(e) => setFilter("amountUsdMax", e.target.value || undefined)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-from">From date</Label>
            <input
              id="filter-from"
              type="date"
              className={INPUT_CLASSNAME}
              value={filters.from ?? ""}
              onChange={(e) => setFilter("from", e.target.value || undefined)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-to">To date</Label>
            <input
              id="filter-to"
              type="date"
              className={INPUT_CLASSNAME}
              value={filters.to ?? ""}
              onChange={(e) => setFilter("to", e.target.value || undefined)}
            />
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={activeCount === 0}
            onClick={() => DIALOG_FILTER_KEYS.forEach((key) => setFilter(key, undefined))}
          >
            Clear these filters
          </Button>
          <Button type="button" size="sm" onClick={() => setOpen(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelPaymentsPage() {
  const tableState = useTableState<ChannelPaymentFilters>({
    defaultFilters: DEFAULT_FILTERS,
    defaultSortBy: "createdAt",
    defaultSortOrder: "desc",
  });

  const params: ListChannelPaymentsParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: (tableState.sortBy ?? undefined) as ListChannelPaymentsParams["sortBy"],
    sortOrder: tableState.sortOrder,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["channel-payments", params],
    queryFn: () => listChannelPayments(params),
  });

  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) => a.businessName.localeCompare(b.businessName));

  const { data: assetOptions } = useQuery({
    queryKey: ["invoice-asset-options"],
    queryFn: getInvoiceAssetOptions,
  });

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  const columns: DataTableColumn<ChannelPaymentListItem>[] = [
    {
      key: "publicId",
      header: "Payment ID",
      cell: (p) => (
        <RowLink channelPaymentId={p.id}>
          <span className="font-mono group-hover:underline">{p.publicId}</span>
        </RowLink>
      ),
    },
    {
      key: "merchant",
      header: "Merchant",
      cell: (p) => (
        <MerchantCell merchantId={p.merchant.id} businessName={p.merchant.businessName} publicId={p.merchant.publicId} />
      ),
    },
    {
      key: "channel",
      header: "Channel",
      cell: (p) => (
        <Link to="/channels/$channelId" params={{ channelId: p.channel.id }} className="group -m-2 block p-2">
          <span className="block group-hover:underline">{p.channel.label}</span>
          <span className="block text-xs text-muted-foreground">{p.channel.publicId}</span>
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (p) => (
        <RowLink channelPaymentId={p.id}>
          <StatusBadge label={formatStatusLabel(p.subStatus ?? p.status)} color={CHANNEL_PAYMENT_STATUS_COLOR[p.status]} />
        </RowLink>
      ),
    },
    {
      key: "asset",
      header: "Asset",
      cell: (p) => (
        <RowLink channelPaymentId={p.id}>
          <AssetLabel symbol={p.asset.symbol} network={p.asset.network} />
        </RowLink>
      ),
    },
    {
      key: "cryptoAmountUsd",
      header: "Amount",
      sortable: true,
      cell: (p) => (
        <RowLink channelPaymentId={p.id}>
          <Amount native={p.cryptoAmount} symbol={p.asset.symbol} usd={p.cryptoAmountUsd} />
        </RowLink>
      ),
    },
    {
      key: "fee",
      header: "Fee",
      cell: (p) => (
        <RowLink channelPaymentId={p.id}>
          <Amount native={p.cryptoFee} symbol={p.asset.symbol} usd={p.feeUsd} />
        </RowLink>
      ),
    },
    {
      key: "senderAddress",
      header: "Sender",
      cell: (p) => <HashLink value={p.senderAddress} type="address" network={p.asset.network} className="text-xs" />,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      cell: (p) => <RowLink channelPaymentId={p.id}>{formatDate(p.createdAt)}</RowLink>,
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        sortBy={tableState.sortBy}
        sortOrder={tableState.sortOrder}
        loading={isPending}
        error={isError ? "Failed to load channel payments." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        onSortChange={(columnKey) => tableState.setSort(columnKey)}
        filterBar={
          <>
            <select
              aria-label="Status"
              className={SELECT_CLASSNAME}
              value={tableState.filters.status ?? "ALL"}
              onChange={(e) =>
                tableState.setFilter(
                  "status",
                  e.target.value === "ALL" ? undefined : (e.target.value as ChannelPaymentStatus),
                )
              }
            >
              <option value="ALL">All statuses</option>
              {Object.values(ChannelPaymentStatus).map((s) => (
                <option key={s} value={s}>
                  {formatStatusLabel(s)}
                </option>
              ))}
            </select>

            <ChannelPaymentFiltersDialog
              filters={tableState.filters}
              setFilter={tableState.setFilter}
              merchants={sortedMerchants}
              assetOptions={assetOptions ?? []}
            />

            {anyFilterActive && (
              <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
                Clear filters
              </Button>
            )}
          </>
        }
        getRowKey={(p) => p.id}
        emptyMessage="No channel payments match your filters."
      />
    </div>
  );
}
